/**
 * "Save and publish" on an edit draft: saves the draft over its live article (save-in-place).
 *
 * - Saves what waits first (the byline, title, body and search fields all go through the autosave,
 *   and the server takes them from the draft).
 * - would_lose: a dialog headed "Saving would lose:" lists each loss as plain text, with
 *   "Remove them and save" (the same save, every key acknowledged) and "Keep editing".
 * - stale_article: says so, and the page reloads the piece to offer starting over.
 */
import { useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { useMutation } from '@tanstack/react-query'
import { errorCode, saveInPlace, type PieceDetail } from '../api'
import { acknowledgeAll, LOSS_HEADING, lossLines, refusalLosses, saveRefusal, type Loss } from './saveInPlace'
import { SAVE_FIRST } from './pieceHelpers'
import { canPublishRefresh, WEB_UNCHECKED_MESSAGE } from '../refreshPages'
import { btn, btnPrimary, errorText } from './ui'

type Props = {
  detail: PieceDetail
  disabled: boolean
  title?: string
  beforeWrite: () => Promise<boolean>
  /** The saved draft (now superseded) and the article's reader URL. */
  onSaved: (d: PieceDetail, url: string | null) => void
  /** The article changed underneath: read the piece again (it then shows as stale). */
  onStale: () => void
  /** The server found a web fact unticked: read the piece again so the ticks shown are the stored ones. */
  onWebUnchecked?: () => void
}

export function SaveAndPublish({ detail: d, disabled, title, beforeWrite, onSaved, onStale, onWebUnchecked }: Props) {
  const [losses, setLosses] = useState<Loss[] | null>(null)
  const save = useMutation({
    mutationFn: async (acknowledged: string[]) => {
      if (!(await beforeWrite())) throw new Error(SAVE_FIRST)
      return saveInPlace(d.id, acknowledged)
    },
    onSuccess: (r) => { setLosses(null); onSaved(r.draft, r.url) },
    onError: (e) => {
      const l = refusalLosses(e)
      if (l) { setLosses(l); return }
      setLosses(null)
      const code = errorCode(e)
      if (code === 'stale_article') onStale()
      if (code === 'web_unchecked') onWebUnchecked?.()
    },
  })
  // A refresh draft waits for every web fact to be ticked; the server holds the same line (web_unchecked).
  const webWait = !canPublishRefresh(d.refresh)
  const failed = save.error && !refusalLosses(save.error)
    ? saveRefusal(save.error).message : null
  return (
    <>
      <button type="button" className={btnPrimary} disabled={disabled || webWait || save.isPending} title={webWait ? WEB_UNCHECKED_MESSAGE : title}
              onClick={() => { save.reset(); save.mutate([]) }}>
        {save.isPending && !losses ? 'Saving…' : 'Save and publish'}
      </button>
      {webWait && <span className="basis-full text-right font-ui text-[13px] text-paper-dim">{WEB_UNCHECKED_MESSAGE}</span>}
      {failed && <p role="alert" className={`${errorText} basis-full text-right`}>{failed}</p>}
      {losses && (
        <LossDialog losses={losses} busy={save.isPending}
                    onConfirm={() => save.mutate(acknowledgeAll(losses))}
                    onClose={() => { setLosses(null); save.reset() }} />
      )}
    </>
  )
}

function LossDialog({ losses, busy, onConfirm, onClose }: { losses: Loss[]; busy: boolean; onConfirm: () => void; onClose: () => void }) {
  const titleId = useId()
  const box = useRef<HTMLDivElement>(null)
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-ink-900/60 px-4 py-12 font-ui" role="presentation"
         onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onClose() }}>
      <div ref={box} role="alertdialog" aria-modal="true" aria-labelledby={titleId} tabIndex={-1}
           onKeyDown={(e) => { if (e.key === 'Escape' && !busy) { e.stopPropagation(); onClose() } }}
           className="flex w-full max-w-[560px] flex-col gap-4 rounded-2xl border border-ink-500 bg-ink-700 px-7 py-6 text-[14px] leading-snug text-paper shadow-xl focus:outline-none">
        <h2 id={titleId} className="m-0 font-serif text-[24px] font-medium leading-tight">{LOSS_HEADING}</h2>
        {/* The server quotes the article's own text and URLs: shown as text only. */}
        <ul className="m-0 flex max-h-[50vh] list-disc flex-col gap-1.5 overflow-y-auto pl-5 text-[14px]">
          {lossLines(losses).map((line, i) => <li key={i} className="break-words">{line}</li>)}
        </ul>
        <p className="m-0 text-[13px] text-paper-dim">Keep editing to put them back, or remove them from the live article.</p>
        <div className="flex flex-wrap justify-end gap-2.5">
          <button type="button" className={btn} disabled={busy} autoFocus onClick={onClose}>Keep editing</button>
          <button type="button" className={btnPrimary} disabled={busy} onClick={onConfirm}>
            {busy ? 'Saving…' : 'Remove them and save'}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
